import classNames from 'classnames';
import { Button } from '@crazyegginc/hatch';

import { Modal } from '/src/contexts/modal';
import { WebP } from '/src/components/WebP';
import { AppLink } from '/src/features/_global/components/AppLink';
import { useManageSubscriptionPath } from '/src/hooks';

import AudienceUpsellPNG from '@crazyegginc/hatch/dist/images/illustration-segment-upsell.png';
import AudienceUpsellWebP from '@crazyegginc/hatch/dist/images/illustration-segment-upsell.webp';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';
import { ReactComponent as DeviceIcon } from '@crazyegginc/hatch/dist/images/icon-mobile-outline.svg';
import { ReactComponent as CountryIcon } from '@crazyegginc/hatch/dist/images/icon-globe-outline.svg';
import { ReactComponent as ConversionIcon } from '@crazyegginc/hatch/dist/images/icon-goal-filled.svg';
import { ReactComponent as ViewedPageIcon } from '@crazyegginc/hatch/dist/images/icon-viewed-page-outline.svg';
import { ReactComponent as CampaignIcon } from '@crazyegginc/hatch/dist/images/icon-campaign-outline.svg';
import { ReactComponent as ClickIcon } from '@crazyegginc/hatch/dist/images/icon-click-outline.svg';
import { ReactComponent as SurveyIcon } from '@crazyegginc/hatch/dist/images/icon-survey-filled.svg';
import { ReactComponent as CurrencyIcon } from '@crazyegginc/hatch/dist/images/icon-currency-outline.svg';
import { ReactComponent as NewReturningIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-undo.svg';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';

export function AudienceUpsellModal({ onBack }) {
  const manageSubscriptionPath = useManageSubscriptionPath();

  return (
    <Modal
      dialogClassName="!w-[800px] !px-[30px]"
      dismissable={onBack ? false : true}
      disableOutsideClick={onBack ? true : false}
    >
      <div className="flex items-center">
        <div className="flex items-center shrink-0">
          <WebP webp={AudienceUpsellWebP} fallback={AudienceUpsellPNG} width="270px" height="245px" />
        </div>
        <div>
          <h2 className="text-header-1 mb-2.5 leading-none">Unlock Advanced Audience Targeting</h2>
          <div className="text-body-5 mb-[25px]">
            Upgrade to reach the right visitors with more precise experiment targeting.
          </div>
          <ColoredBox
            className="shadow-[0_1px_3px_0_rgba(0,0,0,0.05),4px_0_0_0_inset_rgba(138,188,0,1)]"
            left={
              <>
                <div className="text-header-4 mb-2.5 leading-none">Plus</div>
                <div className="text-body-4">Start targeting experiments by device.</div>
              </>
            }
            right={<Criteria icon={DeviceIcon} label="Device" bgColor="bg-lima-500" iconClassName="text-lima-500" />}
          />
          <ColoredBox
            className="shadow-[0_1px_3px_0_rgba(0,0,0,0.05),4px_0_0_0_inset_rgba(186,137,245,1)] mt-2.5"
            left={
              <>
                <div className="text-header-4 mb-2.5 leading-none">Pro</div>
                <div className="text-body-4">Target visitors with precision using advanced audience rules.</div>
              </>
            }
            right={
              <div className="flex items-start space-x-2.5">
                <div className="space-y-2.5">
                  <Criteria
                    icon={DeviceIcon}
                    label="Device"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={CountryIcon}
                    label="Country"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={ConversionIcon}
                    label="Conversion"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={ViewedPageIcon}
                    label="Referrer"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={CampaignIcon}
                    label="UTM params"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                </div>
                <div className="space-y-2.5">
                  <Criteria
                    icon={ClickIcon}
                    label="Clicked CTA"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={SurveyIcon}
                    label="Answered Survey"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={CurrencyIcon}
                    label="Max Worth"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500"
                  />
                  <Criteria
                    icon={NewReturningIcon}
                    label="New or Returning"
                    bgColor="bg-lavender-500"
                    iconClassName="text-lavender-500 scale-x-[-1]"
                  />
                </div>
              </div>
            }
          />
          <ColoredBox
            className="shadow-[0_1px_3px_0_rgba(0,0,0,0.05),4px_0_0_0_inset_rgba(239,153,6,1)] mt-2.5"
            left={
              <>
                <div className="text-header-4 mb-2.5 leading-none">Enterprise</div>
                <div className="text-body-4">Get access to every audience type.</div>
              </>
            }
            right={
              <div className="space-y-2.5">
                <Criteria
                  icon={TickIcon}
                  label="All audience targeting"
                  bgColor="bg-california-500"
                  iconClassName="text-california-500 !w-3 !h-3"
                />
                <Criteria
                  icon={TickIcon}
                  label="Access to all current and future audiences"
                  bgColor="bg-california-500"
                  iconClassName="text-california-500 !w-3 !h-3"
                />
              </div>
            }
          />
          <div className="w-full flex mt-[25px] space-x-2.5">
            {onBack ? (
              <Button variant="cancel" onClick={onBack}>
                <ArrowIcon className="mr-2 h-2.5 w-2.5 -rotate-90 fill-current" />
                Go Back
              </Button>
            ) : null}
            <Button component={AppLink} to={manageSubscriptionPath} target="_blank" rel="noopener noreferrer">
              Upgrade Plan
            </Button>
          </div>
        </div>
      </div>
      {onBack ? (
        <Button variant="cancel" onClick={onBack} className="absolute top-2 left-2">
          <ArrowIcon className="mr-2 h-2.5 w-2.5 -rotate-90 fill-current" />
          Back
        </Button>
      ) : null}
    </Modal>
  );
}

function ColoredBox({ className, left, right }) {
  return (
    <section className={classNames('flex items-center w-full rounded-lg border border-mystic-500 p-3.75', className)}>
      <div className="w-[130px] shrink-0 grow-0">{left}</div>
      <div className="w-px self-stretch border-dashed border-l-mystic-500 border-l mx-2.5" />
      <div>{right}</div>
    </section>
  );
}

function Criteria({ icon: Icon, label, bgColor, iconClassName }) {
  return (
    <div className="flex items-center space-x-2.5">
      <div
        className={classNames(
          'flex items-center justify-center w-[25px] h-[25px] rounded-full bg-opacity-[0.15]',
          bgColor,
        )}
      >
        <Icon className={classNames('fill-current w-3.75 h-3.75', iconClassName)} />
      </div>
      <div className="text-body-5">{label}</div>
    </div>
  );
}
